import React, { useEffect, createRef, useState } from 'react';
import { View, Switch, Text } from 'react-native';
import { useQuery } from '@apollo/client';
import DataTable from '@preflighttech/preflight-tables';
import webStyled from 'styled-components';
import moment from 'moment';

import DataState from 'app/src/components/DataState';
import minimalTable from 'app/src/elements/minimalTable';
import { useParams } from 'app/src/utils/routing';

import { SALE_VIDEO_STREAMS } from './queries';
import { Title, Field, Label } from './elements';

const Audio = webStyled.audio`
  &&::-webkit-media-controls-play-button, &&::-webkit-media-controls-panel {
    background-color: ${props => props.isPlaying ? 'yellow' : 'none'};
  }

  width: 100%;
`;

const Streams = () => {
  const { id } = useParams();
  const [playingIndex, setPlayingIndex] = useState(null);
  const [videoRefs, setVideoRefs] = useState([]);
  const [autoplay, setAutoplay] = useState(true);

  const { data } = useQuery(SALE_VIDEO_STREAMS, { variables: { id } });

  const videosLength = data?.sale.videoStreams.length || 0;

  useEffect(() => {
    setVideoRefs(videoRefs => (
      Array(videosLength).fill().map((_, index) => (
        videoRefs[index] || createRef()
      ))
    ));
  }, [videosLength]);

  const playNext = () => {
    if (!autoplay) { return; }

    const videoRef = videoRefs[playingIndex + 1];
    if (videoRef?.current) {
      videoRef.current.play();
    } else {
      setPlayingIndex(null);
    }
  };

  if (!data) { return <DataState.Loading />; }

  const allStreams = [...data.sale.saleRecordings, ...data.sale.videoStreams]
    .toSorted((a, b) => a.startedAt > b.startedAt ? 1 : -1);

  const columns = [
    {
      key: 'startAt',
      label: 'Start',
      sort: 'prevent',
      content: ({ value }) => (
        moment(value).format('ddd, M/D @ h:mm:ss A')
      )
    },
    {
      key: 'video',
      label: 'Play',
      sort: 'prevent',
      cellStyle: { paddingHorizontal: 0, paddingVertical: 0 },
      value: ({ entry }) => (
        allStreams.findIndex(stream => stream.id === entry.id)
      ),
      content: ({ entry, value }) => {
        const isPlaying = value === playingIndex;

        if (entry.url || entry.video) {
          return (
            <View style={{backgroundColor: isPlaying ? 'yellow' : 'none'}}>
              <Audio
                controls
                src={entry.url || entry.video.url}
                onPlaying={() => setPlayingIndex(value)}
                onEnded={playNext}
                ref={videoRefs[value]}
                style={{ height: 30 }}
                isPlaying={isPlaying}
              />
            </View>
          );
        } else {
          return (
            <View style={{ padding: 8 }}>
              <Text>Recording not available</Text>
            </View>
          );
        }
      }
    }
  ];

  return (
    <View style={{margin: 20}}>
      <Title>{data.sale.name}</Title>

      <Field style={{marginVertical: 10}}>
        <Label>Autoplay next clip?</Label>
        <Switch
          value={autoplay}
          onValueChange={() => setAutoplay(!autoplay)}
        />
      </Field>

      <DataTable.Simple
        data={allStreams}
        columns={columns}
        component={minimalTable}
        pageLength="All"
        htmlTable
      />
    </View>
  );
};

export default Streams;
